import { Grid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import AdminEntityManager from '../../components/admin-entities/admin-entity-manager/admin-entity-manager.component';
import SelectMerchantFilter from '../../components/filters/select-merchant-filter.component';
import SelectPartnerFilter from '../../components/filters/select-partner-filter.component';
import SelectResellerFilter from '../../components/filters/select-reseller-filter.component';
import { Entity } from '../../enums/entity.enums';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import {
  BasicMerchantBranch,
  RetrieveAllEntityRequest,
  RetrieveAllMerchantBranchesResponse,
} from '../../type/entity.types';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';

function AdminBranchesPage() {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const [branchesData, setBranchesData] = useState<BasicMerchantBranch[]>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      resellersData: [],
      merchantsData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      reseller: '',
      merchant: '',
    },
  });
  const [loadBranchesParams, setLoadBranchesParams] =
    useState<RetrieveAllEntityRequest>({
      page: 1,
      limit: 20,
    });
  const [refreshTable, setRefreshTable] = useState(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadBranches = async () => {
    let params: LoadBranchesParams = {
      ...loadBranchesParams,
    };

    if (!loadBranchesParams?.partnerId) {
      delete params.partnerId;
    }

    if (!loadBranchesParams?.resellerId) {
      delete params.resellerId;
    }
    if (!loadBranchesParams?.merchantId) {
      delete params.merchantId;
    }

    delete params.searchEntity;
    if (loadBranchesParams.searchEntity?.trim()) {
      params.branchName = loadBranchesParams.searchEntity;
    } else {
      delete params.branchName;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate.get('/web/merchant_branches', {
        params,
      });

      const data: RetrieveAllMerchantBranchesResponse = response.data;

      const branches: BasicMerchantBranch[] = data?.items;

      setPaginationData(data?.metadata);
      return branches;
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-load-branches-data',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAndSetBranches = async () => {
    const branches = await loadBranches();
    setBranchesData(branches);
  };

  const paginationRequest = (page: number, limit: number) => {
    setLoadBranchesParams((prev) => {
      return {
        ...prev,
        page,
        limit,
      };
    });
  };

  useDidMountEffect(() => {
    let isInvalidFilters = false;
    if (
      auth?.role === Roles.ADMINISTRATOR ||
      auth?.role === Roles.SETTLEMENT ||
      auth?.role === Roles.CHECKER
    ) {
      isInvalidFilters =
        filterValues.values.merchant === loadBranchesParams.merchantId &&
        filterValues.values.reseller === loadBranchesParams.resellerId &&
        filterValues.values.partner === loadBranchesParams.partnerId;
    } else if (auth?.role === Roles.PARTNER) {
      isInvalidFilters =
        filterValues.values.merchant === loadBranchesParams.merchantId &&
        filterValues.values.reseller === loadBranchesParams.resellerId;
    } else if (auth?.role === Roles.RESELLER) {
      isInvalidFilters =
        filterValues.values.merchant === loadBranchesParams.merchantId;
    }

    if (isInvalidFilters) {
      return;
    }

    setLoadBranchesParams((prev) => {
      return {
        ...prev,
        partnerId: filterValues.values.partner,
        resellerId: filterValues.values.reseller,
        merchantId: filterValues.values.merchant,
        page: 1,
      };
    });

    return () => {};
  }, [filterValues.values]);

  useDidMountEffect(() => {
    loadAndSetBranches();
    setCheckedAll(false);
    setIsChecked([]);
  }, [loadBranchesParams]);

  useDidMountEffect(() => {
    loadAndSetBranches();
  }, [refreshTable]);

  useEffect(() => {
    loadAndSetBranches();
  }, []);

  return (
    <>
      <AdminEntityManager
        entityType={Entity.BRANCH}
        branchesData={branchesData}
        paginationRequest={paginationRequest}
        paginationData={paginationData}
        entityParams={loadBranchesParams}
        setEntityParams={setLoadBranchesParams}
        setRefreshTable={setRefreshTable}
        checkedAll={checkedAll}
        setCheckedAll={setCheckedAll}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        filter={
          <>
            <Grid>
              {(auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER) && (
                <Grid.Col xs={12} sm={6}>
                  <SelectPartnerFilter
                    filterSelectData={filterSelectData}
                    filterValues={filterValues}
                  />
                </Grid.Col>
              )}
              {(auth?.role === Roles.PARTNER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER) && (
                <Grid.Col xs={12} sm={6}>
                  <SelectResellerFilter
                    filterSelectData={filterSelectData}
                    filterValues={filterValues}
                    isPartnerDependent={
                      auth?.role === Roles.ADMINISTRATOR ||
                      auth?.role === Roles.SETTLEMENT ||
                      auth?.role === Roles.CHECKER
                    }
                  />
                </Grid.Col>
              )}

              {(auth?.role === Roles.PARTNER ||
                auth?.role === Roles.RESELLER ||
                auth?.role === Roles.ADMINISTRATOR ||
                auth?.role === Roles.SETTLEMENT ||
                auth?.role === Roles.CHECKER) && (
                <Grid.Col xs={12} sm={6}>
                  <SelectMerchantFilter
                    filterSelectData={filterSelectData}
                    filterValues={filterValues}
                    isResellerDependent={
                      auth?.role === Roles.PARTNER ||
                      auth?.role === Roles.ADMINISTRATOR ||
                      auth?.role === Roles.SETTLEMENT ||
                      auth?.role === Roles.CHECKER
                    }
                  />
                </Grid.Col>
              )}
            </Grid>
          </>
        }
        isLoading={isLoading}
      />
    </>
  );
}

export default AdminBranchesPage;

interface LoadBranchesParams extends RetrieveAllEntityRequest {
  branchName?: string;
}
